import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Button,
  Modal,
  Divider,
  Spin,
  Descriptions,
  message,
  Empty,
} from "antd";
import {
  CheckCircleFilled,
  CheckOutlined,
  DownloadOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import styles from "./Payments.module.less";
import { useSelector } from "react-redux";
import { useGetPaymentsQuery } from "../../../redux/services/payments";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const Payments = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const { student_id } = useSelector((state) => state.auth);
  const { data: paymentsData, isLoading } = useGetPaymentsQuery(student_id);

  // Format date and time from API date
  const formatDateTime = (dateString) => {
    const date = dayjs(dateString);
    return {
      date: date.format("DD MMM YYYY"),
      time: date.format("hh:mm A"),
    };
  };

  const showModal = (payment) => {
    setSelectedPayment(payment);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleRefund = (paymentId, e) => {
    e.stopPropagation(); // Prevent card click event
    message.info(`Refund requested for transaction ${paymentId}`);
    // Add actual refund API call here
  };

  const handleDownloadReceipt = () => {
    message.success("Receipt downloading...");
    // Add actual download functionality here
  };

  const handleShareReceipt = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Payment Receipt - ${selectedPayment?.id}`,
          text: `Payment of ${selectedPayment?.amount} to ${selectedPayment?.coaching}`,
          url: window.location.href,
        })
        .catch((error) => message.error("Error sharing receipt"));
    } else {
      message.info("Sharing not supported on this device");
    }
  };

  return (
    <div className={styles.paymentsContainer}>
      <Title level={4} className={styles.sectionTitle}>
        Payment History
      </Title>
      <Spin spinning={isLoading}>
        {paymentsData?.data && paymentsData.data.length > 0 ? (
          paymentsData.data.map((payment) => {
            const { date, time } = formatDateTime(payment.payment_date);
            return (
              <Card
                key={payment.payment_id}
                className={styles.paymentCard}
                onClick={() => showModal(payment)}
              >
                <Row align='middle' justify='space-between' gutter={[16, 0]}>
                  <Col xs={4} sm={3} md={2}>
                    <Avatar
                      className={styles.successAvatar}
                      icon={<CheckOutlined />}
                      size='large'
                    />
                  </Col>
                  <Col xs={14} sm={16} md={18}>
                    <p className={styles.amountText}>Paid ₹{payment.amount}</p>
                    <p className={styles.dateText}>
                      {date} • {time}
                    </p>
                  </Col>
                  <Col xs={6} sm={5} md={4} className={styles.refundCol}>
                    <Button
                      type='primary'
                      onClick={(e) => handleRefund(payment.payment_id, e)}
                    >
                      Refund
                    </Button>
                  </Col>
                </Row>
              </Card>
            );
          })
        ) : (
          <Empty description='No payment records found' />
        )}
      </Spin>

      {selectedPayment && (
        <Modal
          centered
          title={
            <Row align='middle' justify={"flex-start"}>
              <Col xs={4} sm={3} md={2}>
                <Avatar
                  className={styles.successAvatar}
                  icon={<CheckCircleFilled />}
                  size='large'
                />
              </Col>
              <Col xs={14} sm={16} md={18}>
                <p className={styles.amountText}>₹{selectedPayment.amount}</p>
                <p className={styles.dateText}>
                  {formatDateTime(selectedPayment.payment_date).date} •{" "}
                  {formatDateTime(selectedPayment.payment_date).time}
                </p>
              </Col>
            </Row>
          }
          open={isModalVisible}
          onCancel={handleCancel}
          footer={[
            <Button
              key='download'
              icon={<DownloadOutlined />}
              onClick={handleDownloadReceipt}
            >
              Download Receipt
            </Button>,
            <Button
              key='share'
              icon={<ShareAltOutlined />}
              onClick={handleShareReceipt}
            >
              Share Receipt
            </Button>,
          ]}
          width={700}
          className={styles.paymentModal}
        >
          <div className={styles.modalContent}>
            <div className={styles.modalSection}>
              <Row gutter={[24, 16]}>
                <Col xs={12} sm={6}>
                  <Descriptions column={1} size='small' layout='vertical'>
                    <Descriptions.Item label='Transaction Number'>
                      {selectedPayment.transaction_id}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={12} sm={6}>
                  <Descriptions column={1} size='small' layout='vertical'>
                    <Descriptions.Item label='Payment Method'>
                      {selectedPayment.payment_type}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={12} sm={6}>
                  <Descriptions column={1} size='small' layout='vertical'>
                    <Descriptions.Item label='To'>
                      {selectedPayment.batch?.batch_name || "N/A"}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={12} sm={6}>
                  <Descriptions column={1} size='small' layout='vertical'>
                    <Descriptions.Item label='Status'>
                      <Text type='success'>{selectedPayment.status}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </div>

            <Divider className={styles.modalDivider} />

            <div className={styles.modalSection}>
              <Row gutter={[24, 16]}>
                <Col xs={12} sm={6}>
                  <Descriptions column={1} size='small' layout='vertical'>
                    <Descriptions.Item label='Student Name'>
                      {user?.name || "Student Name"}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={12} sm={6}>
                  <Descriptions column={1} size='small' layout='vertical'>
                    <Descriptions.Item label='Course Duration'>
                      {selectedPayment.batch?.start_date &&
                      selectedPayment.batch?.end_date
                        ? `${dayjs(selectedPayment.batch.start_date).format(
                            "MMM YYYY"
                          )} - ${dayjs(selectedPayment.batch.end_date).format(
                            "MMM YYYY"
                          )}`
                        : "N/A"}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={12} sm={6}>
                  <Descriptions column={1} size='small' layout='vertical'>
                    <Descriptions.Item label='Batch Type'>
                      {selectedPayment.batch?.batch_type || "N/A"}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={12} sm={6}>
                  <Button
                    type='primary'
                    danger
                    className={styles.refundButtonModal}
                    onClick={(e) => handleRefund(selectedPayment.payment_id, e)}
                  >
                    Request Refund
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Payments;
